import React, { useState } from 'react';
import styled from 'styled-components';
import { CoreApi, Product, ProductType } from '@inter-ikea-storageone/range';
import { useSelector } from 'react-redux';
import { KompisCarousel, KompisCarouselItem } from '@inter-ikea-kompis/react-components';
import CarouselLayoutEnum from '@inter-ikea-kompis/component-carousel/lib/enums/CarouselLayoutEnum';
import { ProductCardImage, ProductGroup } from 'rangeConfig/@types';
import { hasActiveFilters } from 'ducks/products';
import PillSizeOptionsEnum from '@inter-ikea-kompis/component-pill/lib/enums/PillSizeOptionsEnum';
import { KompisPill } from '@inter-ikea-kompis/react-components';
import { useIntl } from 'react-intl';
import { NoneOption } from 'rangeConfig/@types';
import NoProductsOnFilterMessage from 'components/NoProductsOnFilterMessage/NoProductsOnFilterMessage';
import ProductCard from './ProductCard';
import NoneOptionWidget from './NoneOptionWidget';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';
import { e2e } from 'utils/queryUtils';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useCore } from 'contexts/core';

const CarouselWrapper = styled.div`
  max-width: 100vw;
  overflow: hidden;
  width: 100%;
  padding: 0 ${({ theme }) => theme.space.md};
  list-style-type: none;
`;

export const transformPosition = (isRtl: boolean) =>
  isRtl ? `translateX(-33%)` : `translateX(33%)`;

const PillWrapper = styled.div`
  min-width: 115px;
  padding-left: ${({ theme }) => theme.space.s};
`;

type MobileSwiperProps = {
  id: string;
  numberOfItems: number;
  selectedIds?: string[];
  selected?: (product: Product, coreApi: CoreApi) => boolean;
  onSelect: (product: Product, index: number) => void;
  onAdditionalItemsClicked?: () => void;
  selectedGroup?: ProductGroup;
  productClusters: Product[][];
  loading?: boolean;
  showInfoButton?: boolean;
  mainProductCardImage?: ProductCardImage;
  noneOption?: NoneOption;
  mobileCardText?: string | undefined;
};

export type SwipeEvent = {
  detail: {
    selectedIndex: number;
  };
};

const MobileSwiper = ({
  id,
  numberOfItems,
  selectedIds,
  onSelect,
  selected,
  noneOption,
  onAdditionalItemsClicked,
  selectedGroup,
  productClusters,
  loading,
  mainProductCardImage,
  showInfoButton = true,
  mobileCardText
}: MobileSwiperProps) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSwipe = (event: SwipeEvent) => {
    setSelectedIndex(event.detail.selectedIndex);
  };

  const filtersApplied = useSelector(hasActiveFilters);
  const activeFilter = filtersApplied(selectedGroup);
  const productsToShow = activeFilter ? productClusters : productClusters.slice(0, numberOfItems);

  if (productClusters.length === 0 && !loading) {
    return <NoProductsOnFilterMessage />;
  }

  const isInteriorSuggestion = id === 'interior-suggestions-product-list';

  return (
    <>
      <CarouselWrapper>
        <KompisCarousel
          showArrows={!e2e()}
          selectedIndex={selectedIndex}
          layout={CarouselLayoutEnum.overflow}
          theme={SkapaTheme}
          onChange={onSwipe}
        >
          {noneOption && <NoneOptionWidget noneOption={noneOption} />}
          {isInteriorSuggestion && (
            <NoneOptionWidget noneOption={noneOptionForInteriorSuggestions as NoneOption} />
          )}
          {productsToShow &&
            productsToShow.map((cluster, i) => {
              const product = cluster[0];
              const dataTestId =
                product.type === ProductType.PAX_CORNER_SOLUTION_ASM
                  ? product.coreProductId.split('-')[1]
                  : product.coreProductId;
              const checked =
                selected !== undefined
                  ? selected(product, coreApi)
                  : cluster.some(p => selectedIds?.includes(p.coreProductId));
              return (
                <KompisCarouselItem
                  key={product.coreProductId}
                  data-testid={`swiper-item-${dataTestId}`}
                >
                  <ProductCard
                    data-testid="swiper-product-card"
                    productCluster={cluster}
                    onSelect={() => onSelect(product, i)}
                    selected={checked}
                    mainImage={mainProductCardImage}
                    showInfoButton={showInfoButton}
                    mobileCardText={mobileCardText}
                  />
                </KompisCarouselItem>
              );
            })}
          {onAdditionalItemsClicked && (
            <KompisCarouselItem>
              <PillWrapper>
                <KompisPill
                  data-testid={`${id}-additional-button`}
                  onSelect={onAdditionalItemsClicked}
                  pillSize={PillSizeOptionsEnum.small}
                  label={formatMessage({ id: 'storageone.mobileSwiperViewMore' })}
                  theme={SkapaTheme}
                />
              </PillWrapper>
            </KompisCarouselItem>
          )}
        </KompisCarousel>
      </CarouselWrapper>
    </>
  );
};

export default MobileSwiper;
